import React from 'react'
import { Helmet } from 'react-helmet';
import utils from '../utils';

function TermsPage() {
    const url = typeof window !== 'undefined' ? window.location.href : '';

    return (
        <div>
            <Helmet>
                <title>Terms & Conditions - {utils.siteName}</title>
                <link rel="canonical" href={url} />
            </Helmet>
            <header className="hero-bg text-white py-16">
                <div className="container mx-auto px-4 text-center">
                    <h1 className="text-4xl md:text-6xl font-black">Terms & Conditions</h1>
                    <p className="mt-4 text-lg text-gray-300 max-w-2xl mx-auto">These Terms & Conditions govern your access to and use of the {utils.siteName} platform — including buying, selling, account responsibilities, and legal rights. By using our services, you agree to these terms.</p>
                    <div className="mt-6 mx-auto w-20 h-1.5 bg-secondary rounded-full"></div>
                </div>
            </header>
            <section className="py-16 lg:py-24">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto bg-white dark:bg-slate-900 p-8 md:p-12 rounded-lg shadow-xl">
                        <div className="prose prose-lg dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
                            <h1 className="text-3xl md:text-4xl font-black text-gray-900 dark:text-white mb-6">Terms & Conditions</h1>
                            <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                                These Terms & Conditions govern your use of the Koova marketplace — including buying, selling, account management, and related services. By accessing or using Koova, you agree to be bound by these terms.
                            </p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                1. Introduction
                            </h2>
                            <p><strong>1.1</strong> “Koova” is the trading name for the Koova group companies. Each Koova group company (“Koova”, “we”, “us”) operates an online marketplace consisting of a website and mobile application (“marketplace”), together with supporting IT, logistics, and payment infrastructure, for the sale and purchase of consumer products and services (“products”) within its allocated territory.</p>
                            <p><strong>1.2</strong> These general terms and conditions apply to buyers and sellers on the marketplace and govern your use of the marketplace and related services.</p>
                            <p><strong>1.3</strong> By using the marketplace, you accept these general terms and conditions in full. If you do not agree, you must not use the marketplace.</p>
                            <p><strong>1.4</strong> If you use the marketplace on behalf of a business or other organization, you confirm that:</p>
                            <ul className="list-disc pl-5 space-y-1 ml-4">
                                <li>you have authority to bind that entity;</li>
                                <li>both you and the entity are bound by these terms; and</li>
                                <li>references to “you” include both the individual and the entity.</li>
                            </ul>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                2. Registration and Account
                            </h2>
                            <p><strong>2.1</strong> You must be at least 18 years old to register or use the marketplace.</p>
                            <p><strong>2.2</strong> When creating an account, you agree to:</p>
                            <ul className="list-disc pl-5 space-y-1 ml-4">
                                <li>keep your login credentials confidential;</li>
                                <li>notify Koova immediately of any unauthorized access; and</li>
                                <li>accept responsibility for all activity conducted through your account.</li>
                            </ul>
                            <p><strong>2.3</strong> Accounts are personal and non-transferable. Any third-party access is at your own risk.</p>
                            <p><strong>2.4</strong> Koova may suspend or terminate accounts at its discretion. Where prepaid services or products are canceled without breach by you, refunds will be issued in accordance with Section 4.</p>
                            <p><strong>2.5</strong> You may close your account by contacting Koova under Section 26.</p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                3. Terms and Conditions of Sale
                            </h2>
                            <p><strong>3.1</strong> You acknowledge that:</p>
                            <ul className="list-disc pl-5 space-y-1 ml-4">
                                <li>the marketplace facilitates transactions between buyers and sellers;</li>
                                <li>Koova may accept orders on behalf of sellers;</li>
                                <li>unless stated otherwise, Koova is not a party to contracts between buyers and third-party sellers.</li>
                            </ul>
                            <p><strong>3.2</strong> A binding contract arises between buyer and seller once an order is confirmed.</p>
                            <p><strong>3.3</strong> Seller obligations include:</p>
                            <ul className="list-disc pl-5 space-y-1 ml-4">
                                <li>accurate pricing inclusive of applicable taxes;</li>
                                <li>transparent disclosure of delivery and ancillary charges;</li>
                                <li>delivery of products that are fit for purpose, lawful, and accurately described;</li>
                                <li>lawful ownership or rights to sell the product.</li>
                            </ul>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                4. Returns and Refunds
                            </h2>
                            <p><strong>4.1</strong> Returns are managed in accordance with the Koova Returns Policy, as updated from time to time.</p>
                            <p><strong>4.2</strong> Refunds may be issued at Koova’s discretion, subject to applicable law, via:</p>
                            <ul className="list-disc pl-5 space-y-1 ml-4">
                                <li>original payment method;</li>
                                <li>store credit;</li>
                                <li>mobile money or bank transfer.</li>
                            </ul>
                            <p><strong>4.3</strong> For digital products and perishable goods, refunds apply only to delivery failures unless otherwise stated by the seller.</p>
                            <p><strong>4.4</strong> Policy changes apply prospectively from the date of publication.</p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                5. Payments
                            </h2>
                            <p><strong>5.1</strong> Payments must be made in accordance with Koova’s Payments Information and Guidelines available on the marketplace.</p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                6. Store Credit
                            </h2>
                            <p><strong>6.1</strong> Store credit is governed by the Koova Store Credit Terms and Conditions. Koova may revoke store credit in cases of suspected abuse or fraud.</p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                7. Promotions
                            </h2>
                            <p><strong>7.1</strong> Promotions and competitions are subject to applicable Promotion Terms and Conditions, published where relevant.</p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                8. Rules About Your Content
                            </h2>
                            <p><strong>8.1</strong> “Your content” includes all materials and communications you submit to the marketplace.</p>
                            <p><strong>8.2</strong> Content must be accurate, lawful, respectful, and non-misleading.</p>
                            <p><strong>8.3</strong> Prohibited content includes content that is:</p>
                            <ul className="list-disc pl-5 space-y-1 ml-4">
                                <li>illegal, obscene, discriminatory, abusive, fraudulent, or misleading;</li>
                                <li>infringing intellectual property or privacy rights;</li>
                                <li>spam or deceptive reviews.</li>
                            </ul>
                            <p><strong>8.4</strong> Koova may remove or moderate content at its discretion.</p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                9. Our Rights to Use Your Content
                            </h2>
                            <p><strong>9.1</strong> You grant Koova a worldwide, non-exclusive, royalty-free, irrevocable license to use, publish, adapt, and distribute your content.</p>
                            <p><strong>9.2</strong> You waive moral rights to the extent permitted by law.</p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                10. Use of Website and Mobile Applications
                            </h2>
                            <p><strong>10.1</strong> You may use the marketplace for lawful personal or business transactions only.</p>
                            <p><strong>10.2</strong> You must not:</p>
                            <ul className="list-disc pl-5 space-y-1 ml-4">
                                <li>scrape, hack, reverse-engineer, or overload systems;</li>
                                <li>use automated access without permission;</li>
                                <li>bypass security or access restrictions.</li>
                            </ul>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                11. Copyright and Trademarks
                            </h2>
                            <p><strong>11.1</strong> All intellectual property on the marketplace belongs to Koova or its licensors.</p>
                            <p><strong>11.2</strong> Koova trademarks may not be used without written consent.</p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                12. Data Privacy
                            </h2>
                            <p><strong>12.1</strong> Personal data is processed in accordance with the Koova Privacy Notice and Cookies Notice.</p>
                            <p><strong>12.2</strong> Sellers are responsible for any misuse of buyer data.</p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                13. Due Diligence and Audit Rights
                            </h2>
                            <p><strong>13.1</strong> Koova may conduct compliance, anti-fraud, and AML checks.</p>
                            <p><strong>13.2</strong> You agree to provide information reasonably required for legal or regulatory purposes.</p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                14. Koova’s Role as a Marketplace
                            </h2>
                            <p><strong>14.1</strong> Koova facilitates transactions but is not responsible for third-party seller performance.</p>
                            <p><strong>14.2</strong> Koova does not guarantee uninterrupted availability of the marketplace.</p>
                            <p><strong>14.3</strong> Koova may modify or discontinue services with reasonable notice where practicable.</p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                15. Limitations and Exclusions of Liability
                            </h2>
                            <p><strong>15.1</strong> Nothing excludes liability prohibited by law.</p>
                            <p><strong>15.2</strong> Koova is not liable for:</p>
                            <ul className="list-disc pl-5 space-y-1 ml-4">
                                <li>indirect or consequential losses;</li>
                                <li>business interruption or loss of data;</li>
                                <li>events beyond reasonable control.</li>
                            </ul>
                            <p><strong>15.3</strong> Aggregate liability is capped at amounts paid by you for the relevant transaction.</p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                16. Indemnification
                            </h2>
                            <p>You agree to indemnify Koova against losses arising from:</p>
                            <ul className="list-disc pl-5 space-y-1 ml-4">
                                <li>your breach of these terms;</li>
                                <li>misuse of the marketplace;</li>
                                <li>tax or regulatory non-compliance attributable to you.</li>
                            </ul>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                17. Breaches
                            </h2>
                            <p>Koova may suspend or terminate access, block IPs, or initiate legal action for breaches.</p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                18. Entire Agreement
                            </h2>
                            <p>These terms, together with referenced policies, constitute the entire agreement.</p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                19. Hierarchy
                            </h2>
                            <p>In case of conflict, these Terms prevail over subsidiary policies.</p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                20. Variation
                            </h2>
                            <p>Koova may update these Terms. Changes take effect upon publication.</p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                21. No Waiver
                            </h2>
                            <p>Failure to enforce rights does not constitute waiver.</p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                22. Severability
                            </h2>
                            <p>Invalid provisions do not affect the remaining terms.</p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                23. Assignment
                            </h2>
                            <p>Koova may assign rights and obligations. Users may not without consent.</p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                24. Third Party Rights
                            </h2>
                            <p>No third party may enforce these Terms.</p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                25. Law and Jurisdiction
                            </h2>
                            <p>These Terms are governed by the laws of Kenya, with exclusive jurisdiction of its courts.</p>

                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                26. Our Company Details and Notices
                            </h2>
                            <p><strong>26.1</strong> Contact details are listed in Appendix 1.</p>
                            <p><strong>26.2</strong> Notices may be delivered electronically and are deemed received when sent.</p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default TermsPage